import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { LegacyAppContent } from './LegacyApp';
import { AuthProvider, useAuth } from '../components/Auth/AuthProvider';
import { PartnerAccessSummary } from '../partner/components/PartnerAccessSummary';
import { ProtectedRoute } from '../components/Auth/ProtectedRoute';
import { TenantProvider } from '../components/TenantContext';
import { AuthCallback } from './AuthCallback';
import { LoginForm } from '../components/Auth/LoginForm';
import { Privacy } from './Privacy';
import { useAdminNavFlag } from '../hooks/useAdminNavFlag';
import { AdminShell } from '../admin/AdminShell';
import { RoleBasedRedirect } from '../components/Auth/RoleBasedRedirect';

// Lazy-loaded components for better code splitting and faster initial load
import {
  LazyAdminDashboard,
  LazyUserManagement,
  LazyGroupStoresManager,
  LazyPermissionsManager,
  LazyClassificationLogs,
  LazyAuditLog,
  LazyCustomerEmailsAdmin,
  LazyWarrantyExportsAdmin,
  LazyWarrantyContractsAdmin,
  LazyDecoderPipeline,
  LazyBIC,
  LazyReportsRedesigned,
  LazyOpenTrackMultiTenantReport,
  LazyDealervaultMultiTenantReport,
  LazyChemicalsReport,
  LazySetPassword,
  LazySetupMfa,
  LazyAuthReset,
  LazyAuthMfa,
  // Insurance Partner Portal
  LazyInsurancePartnerShell,
  LazyPartnerDashboard,
  LazyPartnerReportDownload,
  LazyAccountSettingsPage,
  // Insurance Partner Admin Management
  // Accounting Portal
  LazyAccountingShell,
  LazyAccountingDashboard,
  LazyReportDownload,
  LazyAccountCodesManagement,
  LazyDownloadHistory,
  LazyAccountingSettings,
  LazyEmailSchedules,
  LazyDataAlerts,
} from './lazyRoutes';

const LegacyRoute: React.FC = () => <LegacyAppContent />;

const AuthOnlyRoute: React.FC = () => <LoginForm />;

const TenantProviderWithAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin } = useAuth();
  return <TenantProvider isSuperAdmin={isSuperAdmin}>{children}</TenantProvider>;
};

export const App: React.FC = () => {
  const adminNavEnabled = useAdminNavFlag();

  return (
    <AuthProvider>
      <TenantProviderWithAuth>
        <Routes>
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/reset" element={<LazyAuthReset />} />
          <Route path="/auth/set-password" element={<LazySetPassword />} />
          <Route path="/auth/mfa" element={<LazyAuthMfa />} />
          <Route path="/setup-mfa" element={<LazySetupMfa />} />

          {adminNavEnabled ? (
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute requirePortal="admin">
                  <AdminShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<LazyAdminDashboard />} />
              <Route path="users" element={<LazyUserManagement />} />
              <Route path="groups-stores" element={<LazyGroupStoresManager />} />
              <Route path="permissions" element={<LazyPermissionsManager />} />
              {/* Backwards/typo-safe redirect (prevents blank screen on /admin/admin/observability) */}
              <Route
                path="admin/observability"
                element={<Navigate to="/admin/observability" replace />}
              />
              <Route
                path="decoder-pipeline"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <LazyDecoderPipeline />
                  </ProtectedRoute>
                }
              />
              <Route path="classification-logs" element={<LazyClassificationLogs />} />
              <Route path="audit-log" element={<LazyAuditLog />} />
              <Route path="bic" element={<LazyBIC />} />
              <Route
                path="customer-emails"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <LazyCustomerEmailsAdmin />
                  </ProtectedRoute>
                }
              />
              <Route path="warranty" element={<Navigate to="/admin/warranty/exports" replace />} />
              <Route
                path="warranty/contracts"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <LazyWarrantyContractsAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="warranty/exports"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <LazyWarrantyExportsAdmin />
                  </ProtectedRoute>
                }
              />
              <Route path="account" element={<LazyAccountSettingsPage />} />
              {/* Prevent blank admin shell for unknown admin routes */}
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>
          ) : (
            <>
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <LegacyRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <LegacyRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/groups-stores"
                element={
                  <ProtectedRoute>
                    <LegacyRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/permissions"
                element={
                  <ProtectedRoute>
                    <LegacyRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/parity"
                element={
                  <ProtectedRoute>
                    <LegacyRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bic"
                element={
                  <ProtectedRoute>
                    <LegacyRoute />
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* Insurance Partner Portal Routes */}
          <Route
            path="/partner/*"
            element={
              <ProtectedRoute requirePortal="insurance">
                <LazyInsurancePartnerShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<LazyPartnerDashboard />} />
            <Route path="reports" element={<LazyPartnerReportDownload />} />
            <Route
              path="account"
              element={<LazyAccountSettingsPage sidebarExtras={<PartnerAccessSummary />} />}
            />
            <Route path="*" element={<Navigate to="/partner" replace />} />
          </Route>

          {/* Accounting Portal Routes */}
          <Route
            path="/accounting/*"
            element={
              <ProtectedRoute requirePortal="accounting">
                <LazyAccountingShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<LazyAccountingDashboard />} />
            <Route path="reports" element={<LazyReportDownload />} />
            <Route path="account-codes" element={<LazyAccountCodesManagement />} />
            <Route path="history" element={<LazyDownloadHistory />} />
            <Route path="email-schedules" element={<LazyEmailSchedules />} />
            <Route path="alerts" element={<LazyDataAlerts />} />
            <Route path="settings" element={<LazyAccountingSettings />} />
            <Route path="account" element={<LazyAccountSettingsPage />} />
            <Route path="*" element={<Navigate to="/accounting" replace />} />
          </Route>

          <Route
            path="/bic"
            element={
              <ProtectedRoute>
                <LegacyRoute />
              </ProtectedRoute>
            }
          />

          {/* New redesigned reports page */}
          <Route
            path="/reports-new"
            element={
              <ProtectedRoute>
                <LazyReportsRedesigned />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/covered-parts"
            element={
              <ProtectedRoute requireFeature="parts">
                <LazyOpenTrackMultiTenantReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/dealervault"
            element={
              <ProtectedRoute requireFeature="parts">
                <LazyDealervaultMultiTenantReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/chemicals"
            element={
              <ProtectedRoute requireFeature="chemicals">
                <LazyChemicalsReport />
              </ProtectedRoute>
            }
          />

          {/* Redirect root to OpenTrack report
          <Route path="/" element={<Navigate to="/reports/covered-parts" replace />} /> */}

          {/* Role-based landing page redirect */}
          <Route path="/" element={<RoleBasedRedirect />} />

          {/* Legacy OLSG Daily Report at /reports path */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <LegacyRoute />
              </ProtectedRoute>
            }
          />

          {/* Auth fallback for guarded routes */}
          <Route path="/login" element={<AuthOnlyRoute />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </TenantProviderWithAuth>
    </AuthProvider>
  );
};
